import React, { useState, useEffect } from "react";

// Hero "How it works" signal loop. Three numbered step cards (1 journal →
// 2 explore → 3 signals) sit on an elliptical circuit. The gap between 1
// and 2 carries the three journal pages (priming → planning → processing),
// and the five-stage signal chain runs along the rest of the track, back
// round to the journal, where the loop completes itself. Cards show their
// title only; hovering/focusing anything reveals its copy in the shared
// centre panel — card 3 reveals the signal definition.
const STEP_CARDS = [
  {
    badge: "1",
    title: "Journal your days",
    body: "Mindfully plan and journal in your NoWastedDays, aiming for the most authentic days possible.",
  },
  {
    badge: "2",
    title: "Open your explore page",
    body: "The explore page is a personal insight space built entirely for your increasing self-knowledge.",
  },
  {
    badge: "3",
    title: "Follow your signals",
    body: "A signal is an objective, unemotional nudge towards greater self-knowledge. They can be found in the explore page by looking at your journal entries or at the data that describesyour days.",
    isDefinition: true,
  },
];

const JOURNAL_PAGES = [
  {
    marker: "i",
    label: "Priming the Day",
    desc: "Open the day gently: score how you feel, then prime yourself with a thank you, a way to detach from self, and why you are pleased to live in your mind & body.",
    q: "“Today, I would like to say thank you to…”",
  },
  {
    marker: "ii",
    label: "My Plan for the Day",
    desc: "Plan your day in five moments — the building blocks of a NoWastedDay — and score each one as you live it.",
    q: "“Slow mindful first hour of the day; tea, stretch, no phone.”",
  },
  {
    marker: "iii",
    label: "Reflecting on the Day",
    desc: "Close the day by capturing the memories that will keep it forever, leaving behind the thoughts and feelings you don't want to carry, and scoring how you feel after it all.",
    q: "“The memories from today that will capture it forever are…”",
  },
];

const INTRO = {
  eyebrow: "The signal loop",
  title: "Learn about yourself safely and smartly",
  hint: "Journal daily, explore your data, and let the signals you spot guide what you do next. Hover any point on the loop to follow the chain.",
};

const SIGNAL_STAGES = [
  {
    label: "Spot a signal",
    desc: "You spot a signal.",
    q: "“Oh, cool, I had my top feeling score that day.”",
  },
  {
    label: "Ask a better question",
    desc: "This signal sparks and helps you ask a better question.",
    q: "“I wonder why that day in particular was so good?”",
  },
  {
    label: "Explore with purpose",
    desc: "The question helps inspire a more purposeful and specific exploration of your journal and data.",
    q: "“Looking through that day, it seems that the joy came from freeing myself to be creative without any pressure, and then spending time with friends.”",
  },
  {
    label: "Inspire new action",
    desc: "This exploration then either produces a new signal, an ever more specific and purposeful question, or an observation that can inspire new action.",
    q: "“I will look to make more time for pressure-less creativity; maybe a minimum of 1 day a month.”",
  },
  {
    label: "New moments, new signals",
    desc: "These new actions then become new moments or memories in your journal and will, in time, become new signals of their own — the loop completes itself.",
    q: "“I think today is time for another one of those creative days!”",
  },
];

// Ellipse: centre (500, 280), rx 430, ry 200 in a 1000×560 viewBox. The
// stage's aspect-ratio matches the viewBox, so these percentages land on
// the same points as the SVG track. Cards: 1 at θ=270° (top), 2 at θ=38°,
// 3 at θ=75°. Journal pages fill the 1→2 gap at θ = 302°, 334°, 6°.
// Chain markers at θ = 105°, 136°, 164°, 196°, 230°.
const STEP_POS = [
  { left: "50%", top: "14.3%" },
  { left: "83.9%", top: "72%" },
  { left: "61.1%", top: "84.5%" },
];
const PAGE_POS = [
  { left: "72.8%", top: "19.7%" },
  { left: "88.6%", top: "34.3%" },
  { left: "92.8%", top: "53.7%" },
];
const SIG_POS = [
  { left: "38.9%", top: "84.5%" },
  { left: "19.1%", top: "74.8%" },
  { left: "8.7%", top: "59.8%" },
  { left: "8.7%", top: "40.2%" },
  { left: "22.4%", top: "22.6%" },
];

const canMatchMedia = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => canMatchMedia() && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (!canMatchMedia()) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

function PanelContent({ activeKey }) {
  if (activeKey && activeKey.startsWith("step-")) {
    const step = STEP_CARDS[Number(activeKey.slice(5))];
    if (step.isDefinition) {
      return (
        <>
          <div className="hiwl-panel-head">What is a signal?</div>
          <div className="hiwl-panel-title">{step.body}</div>
        </>
      );
    }
    return (
      <>
        <div className="hiwl-panel-head">Step {step.badge}</div>
        <div className="hiwl-panel-title">{step.title}</div>
        <p className="hiwl-panel-desc">{step.body}</p>
      </>
    );
  }
  if (activeKey && activeKey.startsWith("page-")) {
    const i = Number(activeKey.slice(5));
    const p = JOURNAL_PAGES[i];
    return (
      <>
        <div className="hiwl-panel-head">
          Inside the journal — page {i + 1} of {JOURNAL_PAGES.length}
        </div>
        <div className="hiwl-panel-title">{p.label}</div>
        <p className="hiwl-panel-desc">{p.desc}</p>
        <p className="hiwl-panel-q">{p.q}</p>
      </>
    );
  }
  if (activeKey && activeKey.startsWith("sig-")) {
    const i = Number(activeKey.slice(4));
    const s = SIGNAL_STAGES[i];
    return (
      <>
        <div className="hiwl-panel-head">
          Signal chain — {i + 1} of {SIGNAL_STAGES.length}
        </div>
        <div className="hiwl-panel-title">{s.label}</div>
        <p className="hiwl-panel-desc">{s.desc}</p>
        <p className="hiwl-panel-q">{s.q}</p>
      </>
    );
  }
  return (
    <>
      <div className="hiwl-panel-head">{INTRO.eyebrow}</div>
      <div className="hiwl-panel-title">{INTRO.title}</div>
      <p className="hiwl-panel-def">{INTRO.hint}</p>
    </>
  );
}

function OrbitLayout({ activeKey, setActiveKey, reducedMotion }) {
  const select = (key) => () => setActiveKey(key);
  return (
    <div className="hiwl-stage" onMouseLeave={() => setActiveKey(null)}>
      <svg
        className="hiwl-svg"
        viewBox="0 0 1000 560"
        aria-hidden="true"
        focusable="false"
      >
        <path
          id="hiwl-orbit"
          className="hiwl-track"
          d="M 500 80 A 430 200 0 0 1 500 480 A 430 200 0 0 1 500 80"
        />
        {/* final segment (last stage → journal): the loop completing itself */}
        <path
          className="hiwl-return-arc"
          d="M 223.6 126.8 A 430 200 0 0 1 500 80"
        />
        {reducedMotion ? (
          <g className="hiwl-dot" transform="translate(500 80)">
            <circle r="13" className="hiwl-dot-halo" />
            <circle r="5.5" className="hiwl-dot-core" />
          </g>
        ) : (
          <g className="hiwl-dot">
            <circle r="13" className="hiwl-dot-halo" />
            <circle r="5.5" className="hiwl-dot-core" />
            <animateMotion dur="16s" repeatCount="indefinite">
              <mpath href="#hiwl-orbit" />
            </animateMotion>
          </g>
        )}
      </svg>
      <span className="hiwl-loop-lbl" aria-hidden="true">
        the loop repeats
      </span>
      {STEP_CARDS.map((step, i) => {
        const key = `step-${i}`;
        return (
          <button
            type="button"
            key={key}
            className={`hiwl-node${activeKey === key ? " is-active" : ""}`}
            style={STEP_POS[i]}
            aria-expanded={activeKey === key}
            aria-controls="hiwl-panel"
            onMouseEnter={select(key)}
            onFocus={select(key)}
            onClick={select(key)}
          >
            <span className="hiwl-node-head">
              <span className="hiwl-badge">{step.badge}</span>
              <span className="hiwl-node-title">{step.title}</span>
            </span>
          </button>
        );
      })}
      {JOURNAL_PAGES.map((p, i) => {
        const key = `page-${i}`;
        return (
          <button
            type="button"
            key={key}
            className={`hiwl-sig hiwl-page${
              activeKey === key ? " is-active" : ""
            }`}
            style={PAGE_POS[i]}
            aria-expanded={activeKey === key}
            aria-controls="hiwl-panel"
            onMouseEnter={select(key)}
            onFocus={select(key)}
            onClick={select(key)}
          >
            <span className="hiwl-sig-n">{p.marker}</span>
            {p.label}
          </button>
        );
      })}
      {SIGNAL_STAGES.map((s, i) => {
        const key = `sig-${i}`;
        return (
          <button
            type="button"
            key={key}
            className={`hiwl-sig${activeKey === key ? " is-active" : ""}`}
            style={SIG_POS[i]}
            aria-expanded={activeKey === key}
            aria-controls="hiwl-panel"
            onMouseEnter={select(key)}
            onFocus={select(key)}
            onClick={select(key)}
          >
            <span className="hiwl-sig-n">{i + 1}</span>
            {s.label}
          </button>
        );
      })}
      <div className="hiwl-panel" id="hiwl-panel" role="group">
        {/* key re-mounts the inner on change → CSS entry animation */}
        <div className="hiwl-panel-inner" key={activeKey || "intro"}>
          <PanelContent activeKey={activeKey} />
        </div>
      </div>
    </div>
  );
}

function StackLayout({ activeKey, setActiveKey, reducedMotion }) {
  const toggle = (key) => () =>
    setActiveKey((prev) => (prev === key ? null : key));
  const renderStep = (step, i) => {
    const key = `step-${i}`;
    const open = activeKey === key;
    return (
      <li key={key} className="hiwl-stack-item">
        <button
          type="button"
          className={`hiwl-node${open ? " is-active" : ""}`}
          aria-expanded={open}
          aria-controls={`hiwl-acc-${key}`}
          onClick={toggle(key)}
        >
          <span className="hiwl-node-head">
            <span className="hiwl-badge">{step.badge}</span>
            <span className="hiwl-node-title">{step.title}</span>
          </span>
        </button>
        <div
          className={`hiwl-acc${open ? " is-open" : ""}`}
          id={`hiwl-acc-${key}`}
        >
          <div className="hiwl-acc-inner">
            <p className="hiwl-panel-desc">{step.body}</p>
          </div>
        </div>
      </li>
    );
  };
  const renderPage = (p, i) => {
    const key = `page-${i}`;
    const open = activeKey === key;
    return (
      <li key={key} className="hiwl-stack-item">
        <button
          type="button"
          className={`hiwl-sig hiwl-page${open ? " is-active" : ""}`}
          aria-expanded={open}
          aria-controls={`hiwl-acc-${key}`}
          onClick={toggle(key)}
        >
          <span className="hiwl-sig-n">{p.marker}</span>
          {p.label}
        </button>
        <div
          className={`hiwl-acc${open ? " is-open" : ""}`}
          id={`hiwl-acc-${key}`}
        >
          <div className="hiwl-acc-inner">
            <p className="hiwl-panel-desc">{p.desc}</p>
            <p className="hiwl-panel-q">{p.q}</p>
          </div>
        </div>
      </li>
    );
  };
  return (
    <ol className="hiwl-stack">
      {!reducedMotion && <span className="hiwl-stack-dot" aria-hidden="true" />}
      {renderStep(STEP_CARDS[0], 0)}
      {JOURNAL_PAGES.map(renderPage)}
      {STEP_CARDS.slice(1).map((step, i) => renderStep(step, i + 1))}
      <li className="hiwl-stack-intro">
        <div className="hiwl-panel-head">{INTRO.eyebrow}</div>
        <div className="hiwl-panel-title">{INTRO.title}</div>
        <p className="hiwl-panel-def">
          Journal daily, explore your data, and let the signals you spot guide
          what you do next — tap any stage to follow the chain.
        </p>
      </li>
      {SIGNAL_STAGES.map((s, i) => {
        const key = `sig-${i}`;
        const open = activeKey === key;
        return (
          <li key={key} className="hiwl-stack-item">
            <button
              type="button"
              className={`hiwl-sig${open ? " is-active" : ""}`}
              aria-expanded={open}
              aria-controls={`hiwl-acc-${key}`}
              onClick={toggle(key)}
            >
              <span className="hiwl-sig-n">{i + 1}</span>
              {s.label}
            </button>
            <div
              className={`hiwl-acc${open ? " is-open" : ""}`}
              id={`hiwl-acc-${key}`}
            >
              <div className="hiwl-acc-inner">
                <p className="hiwl-panel-desc">{s.desc}</p>
                <p className="hiwl-panel-q">{s.q}</p>
              </div>
            </div>
          </li>
        );
      })}
      <li className="hiwl-stack-return" aria-hidden="true">
        <span className="hiwl-loop-lbl">the loop completes</span>
      </li>
    </ol>
  );
}

function HowItWorksLoop() {
  const isDesktop = useMediaQuery("(min-width: 1101px)");
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [activeKey, setActiveKey] = useState(null);

  return (
    <div className="hiwl">
      <h2>How it works</h2>
      {isDesktop ? (
        <OrbitLayout
          activeKey={activeKey}
          setActiveKey={setActiveKey}
          reducedMotion={reducedMotion}
        />
      ) : (
        <StackLayout
          activeKey={activeKey}
          setActiveKey={setActiveKey}
          reducedMotion={reducedMotion}
        />
      )}
    </div>
  );
}

export default HowItWorksLoop;
